import React, { useState, useEffect } from 'react';
import {
  CheckCircle2,
  XCircle,
  Lock,
  ChevronRight,
  ChevronDown,
  X,
  List,
} from 'lucide-react';
import { showWarningToast } from '@/utils/errorHandler';

export interface TocItem {
  lessonId: string;
  lessonTitle: string;
  pageId: string;
  pageTitle: string;
  status: 'completed' | 'incomplete' | 'locked';
  isActive: boolean;
}

export interface TocUnit {
  unitNo: number;
  title: string;
  items: TocItem[];
}

interface CourseTocDrawerProps {
  open: boolean;
  onClose: () => void;
  units: TocUnit[];
  onSelect: (pageId: string, lessonId: string) => void;
}

function getStatusIcon(status: TocItem['status'], isActive: boolean) {
  const base = 'w-5 h-5 shrink-0';
  switch (status) {
    case 'completed':
      return <CheckCircle2 className={`${base} ${isActive ? 'text-white' : 'text-green-600'}`} />;
    case 'incomplete':
      return <XCircle className={`${base} ${isActive ? 'text-white' : 'text-red-500'}`} />;
    case 'locked':
      return <Lock className={`${base} ${isActive ? 'text-white/80' : 'text-gray-400'}`} />;
  }
}

export const CourseTocDrawer: React.FC<CourseTocDrawerProps> = ({
  open,
  onClose,
  units,
  onSelect,
}) => {
  const [expandedUnits, setExpandedUnits] = useState<Set<number>>(new Set());

  // Expand unit that contains the active item
  const activeUnitNo = units.find((u) => u.items.some((i) => i.isActive))?.unitNo;
  useEffect(() => {
    if (activeUnitNo != null) {
      setExpandedUnits((prev) => new Set(prev).add(activeUnitNo));
    }
  }, [activeUnitNo]);

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const toggleUnit = (unitNo: number) => {
    setExpandedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(unitNo)) next.delete(unitNo);
      else next.add(unitNo);
      return next;
    });
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/35 backdrop-blur-sm"
        aria-hidden
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-[360px] bg-white dark:bg-gray-900 shadow-xl flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Page Titles"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2">
            <List className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Page Titles
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Units accordion */}
        <div className="flex-1 overflow-y-auto p-2">
          {units.map((unit) => {
            const isExpanded = expandedUnits.has(unit.unitNo);
            const completedCount = unit.items.filter((i) => i.status === 'completed').length;
            const totalCount = unit.items.length;
            const progressLabel = `${completedCount}/${totalCount}`;

            return (
              <div
                key={unit.unitNo}
                className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-2"
              >
                {/* Unit header */}
                <button
                  type="button"
                  onClick={() => toggleUnit(unit.unitNo)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <span className="font-medium text-gray-900 dark:text-white">
                    {unit.title}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {progressLabel}
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                </button>

                {/* Unit items */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    {unit.items.map((item, idx) => {
                      const isLocked = item.status === 'locked';
                      return (
                        <button
                          key={`${item.lessonId}-${item.pageId}`}
                          type="button"
                          aria-disabled={isLocked}
                          onClick={() => {
                            if (isLocked) {
                              showWarningToast('முதலில் முந்தைய பாடத்தை முடிக்கவும்');
                              return;
                            }
                            onSelect(item.pageId, item.lessonId);
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            item.isActive
                              ? 'bg-[#5b8f12] text-white'
                              : isLocked
                                ? 'opacity-60 bg-gray-50 dark:bg-gray-800/30 text-gray-500 cursor-not-allowed'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <span className="text-sm font-medium w-6 shrink-0">
                            {idx + 1}.
                          </span>
                          <span className="flex-1 truncate text-sm">
                            {item.pageTitle || item.lessonTitle}
                          </span>
                          {getStatusIcon(item.status, item.isActive)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {units.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 p-4">
              No content yet.
            </p>
          )}
        </div>
      </div>
    </>
  );
};
